/**
 * Virtual Table Hook
 * Theme-aware virtual scrolling specifically for data tables
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useVirtualScroll } from './use-virtual-scroll';
/**
 * Theme-aware virtual table hook
 */
export function useVirtualTable({ data, columns, isCompact, height, maxHeight, itemHeight, overscan = 5, onRowClick, onRowDoubleClick, getRowKey = (item, index) => item?.id ?? index, estimateRowHeight }) {
    const scrollElementRef = useRef(null);
    const [isScrolling] = useState(false);
    // Calculate row height based on theme
    const defaultRowHeight = useMemo(() => {
        if (itemHeight)
            return itemHeight;
        return isCompact ? 40 : 48; // Same as original DataTable
    }, [itemHeight, isCompact]);
    // Estimate size function that respects theme
    const estimateSize = useCallback((index) => {
        if (estimateRowHeight) {
            return estimateRowHeight(index);
        }
        return defaultRowHeight;
    }, [defaultRowHeight, estimateRowHeight]);
    // Get scroll element
    const getScrollElement = useCallback(() => {
        return scrollElementRef.current;
    }, []);
    // Virtual scrolling
    const { virtualItems, totalSize, scrollToIndex, scrollToOffset, measureElement } = useVirtualScroll({
        count: data.length,
        getScrollElement,
        estimateSize,
        overscan
    });
    // Container props with theme-aware styling
    const containerProps = useMemo(() => ({
        ref: scrollElementRef,
        style: {
            height: typeof height === 'number' ? `${height}px` : height,
            maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
            overflow: 'auto',
            position: 'relative'
        },
        className: 'virtual-table-container'
    }), [height, maxHeight]);
    // Get props for individual rows
    const getRowProps = useCallback((virtualItem, item) => {
        const rowKey = getRowKey(item, virtualItem.index);
        return {
            key: String(rowKey),
            props: {
                'data-index': virtualItem.index,
                ref: measureElement,
                style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`
                },
                onClick: onRowClick ? () => onRowClick(item, virtualItem.index) : undefined,
                onDoubleClick: onRowDoubleClick ? () => onRowDoubleClick(item, virtualItem.index) : undefined,
                className: 'virtual-table-row'
            }
        };
    }, [getRowKey, measureElement, onRowClick, onRowDoubleClick]);
    // Scroll utilities
    const scrollToRow = useCallback((index, align = 'start') => {
        scrollToIndex(index, { align });
    }, [scrollToIndex]);
    const scrollToTop = useCallback(() => {
        scrollToOffset(0);
    }, [scrollToOffset]);
    return {
        virtualItems,
        totalSize,
        scrollElementRef,
        containerProps,
        getRowProps,
        scrollToRow,
        scrollToTop,
        isScrolling
    };
}
